import React, { useCallback, useEffect, useRef, useState } from "react";

import messageService from "src/services/messageService";
import ChatEmpty from "./component/ChatEmpty";
import ChatHeader from "./component/ChatHeader";
import MessageList from "./component/MessageList";
import conversationService from "src/services/conversationService";
import useMessageComposer from "./hooks/useMessageComposer";

const sameMessage = (a, b) =>
  a.id === b.id ||
  (a.meta_message_id &&
    b.meta_message_id &&
    a.meta_message_id === b.meta_message_id);
const sentAt = (message) => new Date(message.meta_timestamp || message.created_at).getTime();

function ChatWindow({
  activeConversation,
  phoneNumberId: selectedPhoneNumberId,
  onProfileClick,
  onBack,
  onConversationDeleted,
  onConversationRead,
  refreshVersion = 0,
  onAccessLost,
  realtimeConnected,
}) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [messages, setMessages] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [showTemplateSend, setShowTemplateSend] = useState(false);

  const phoneNumberId =
    selectedPhoneNumberId || activeConversation?.phone_number_id ||
    localStorage.getItem("phoneNumberId");
  const markedReadRef = useRef(null);
  const messageSequence = useRef(0);
  const loadedConversation = useRef(null);
  // Older pages are fetched on demand when the reader scrolls up; refreshes keep them.
  const olderPages = useRef({ conversation: null, page: 1, hasMore: false, loading: false });
  const [olderState, setOlderState] = useState({ hasMore: false, loading: false, error: '' });
  const onAccessLostRef = useRef(onAccessLost);
  onAccessLostRef.current = onAccessLost;

  const markRead = useCallback(async () => {
    const id = activeConversation?.id;
    if (!id || !phoneNumberId || markedReadRef.current === id) return;
    markedReadRef.current = id;
    try {
      await conversationService.markAsRead(phoneNumberId, id);
      onConversationRead?.(id);
    } catch (error) {
      markedReadRef.current = null; // let it retry
      console.error("Unable to mark conversation as read:", error);
    }
  }, [activeConversation?.id, phoneNumberId, onConversationRead]);

  const upsertMessage = useCallback((incoming) => {
    if (!incoming?.id) return;
    setMessages((previous) => {
      const index = previous.findIndex((m) => sameMessage(m, incoming));
      if (index >= 0) {
        const next = [...previous];
        next[index] = { ...next[index], ...incoming };
        return next;
      }
      const next = [...previous, incoming];
      next.sort(
        (a, b) =>
          new Date(a.meta_timestamp || a.created_at) -
          new Date(b.meta_timestamp || b.created_at),
      );
      return next;
    });
  }, []);

  const loadMessages = useCallback(async () => {
    if (!activeConversation?.id) return;
    const version = ++messageSequence.current;
    try {
      // Realtime, focus and recovery reads must not blank an open chat.
      if (loadedConversation.current !== activeConversation.id) setLoading(true);
      const response = await messageService.index(activeConversation.id);
      if (version !== messageSequence.current) return;
      const records = response?.data?.data ?? [];
      const pagination = response?.data?.pagination;

      records.sort((a, b) => sentAt(a) - sentAt(b));

      const refreshing = loadedConversation.current === activeConversation.id;
      if (refreshing) {
        // The newest page replaces the recent window; earlier pages the reader opened stay.
        const oldest = records.length ? sentAt(records[0]) : Infinity;
        setMessages((previous) => [
          ...previous.filter((m) => sentAt(m) < oldest && !records.some((r) => sameMessage(r, m))),
          ...records,
        ]);
      } else {
        setMessages(records);
      }
      const paging = olderPages.current;
      if (!refreshing || paging.conversation !== activeConversation.id || paging.page === 1) {
        const hasMore = Boolean(pagination && pagination.current_page < pagination.last_page);
        olderPages.current = { conversation: activeConversation.id, page: 1, hasMore, loading: false };
        setOlderState({ hasMore, loading: false, error: '' });
      }
      loadedConversation.current = activeConversation.id;
      setLoadError('');
    } catch (error) {
      if (version !== messageSequence.current) return;
      if ([401, 403, 404].includes(error?.response?.status)) {
        setMessages([]);
        loadedConversation.current = null;
        setLoadError('This conversation is no longer available.');
        onAccessLostRef.current?.();
      } else {
        setLoadError(loadedConversation.current === activeConversation.id
          ? 'Messages could not refresh. Your current chat is still open.'
          : 'Messages could not load. Please retry.');
      }
    } finally {
      if (version === messageSequence.current) setLoading(false);
    }
  }, [activeConversation?.id]);

  const loadOlder = useCallback(async () => {
    const id = activeConversation?.id;
    const paging = olderPages.current;
    if (!id || paging.conversation !== id || !paging.hasMore || paging.loading) return;
    paging.loading = true;
    setOlderState((state) => ({ ...state, loading: true, error: '' }));
    try {
      const response = await messageService.index(id, { page: paging.page + 1 });
      if (olderPages.current !== paging) return;
      const records = response?.data?.data ?? [];
      const pagination = response?.data?.pagination;
      // New arrivals shift page offsets, so a page can repeat rows already shown; never a gap.
      setMessages((previous) => [
        ...records.filter((r) => !previous.some((m) => sameMessage(m, r))),
        ...previous,
      ].sort((a, b) => sentAt(a) - sentAt(b)));
      paging.page = pagination?.current_page ?? paging.page + 1;
      paging.hasMore = pagination ? pagination.current_page < pagination.last_page : records.length > 0;
      setOlderState({ hasMore: paging.hasMore, loading: false, error: '' });
    } catch (error) {
      if (olderPages.current !== paging) return;
      setOlderState({ hasMore: paging.hasMore, loading: false, error: 'Earlier messages could not load.' });
    } finally {
      paging.loading = false;
    }
  }, [activeConversation?.id]);

  const cancelPending = useCallback(() => {
    messageSequence.current++;
  }, []);
  useEffect(() => {
    setReplyTo(null);
    setShowTemplateSend(false);
    setMessages([]);
    setLoadError('');
    olderPages.current = { conversation: null, page: 1, hasMore: false, loading: false };
    setOlderState({ hasMore: false, loading: false, error: '' });
    loadedConversation.current = null;
    markedReadRef.current = null;
    return cancelPending;
  }, [activeConversation?.id, cancelPending]);

  useEffect(() => {
    if (!activeConversation?.id) return undefined;
    loadMessages();
    if (
      Number(activeConversation.unread_count || 0) > 0 &&
      document.hasFocus()
    ) {
      markedReadRef.current = null;
      markRead();
    }
    return cancelPending;
    // Re-fetch after an invalidation; the server checks the current assignee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id, refreshVersion, loadMessages, cancelPending]);

  // When a message is sent, append the server's copy immediately so the UI
  // updates without a full refetch; the realtime echo then de-dupes by id.
  const handleMessageSent = useCallback(
    (createdMessage) => {
      if (createdMessage?.id) {
        upsertMessage(createdMessage);
      } else {
        loadMessages();
      }
    },
    [upsertMessage, loadMessages],
  );

  const { handleSendTemplate } = useMessageComposer({
    conversation: activeConversation,
    onMessageSent: handleMessageSent,
    onCancelReply: () => setReplyTo(null),
    replyTo,
  });

  const handleTemplateClick = () => {
    setShowTemplateSend(true);
  };

  const handleCloseTemplate = () => {
    setShowTemplateSend(false);
  };

  const handleTemplateSent = async () => {
    setShowTemplateSend(false);
    await loadMessages();
  };

  const latestIncomingMessage = [...messages]
    .reverse()
    .find((message) => message.direction === "Incoming");

  // WhatsApp lets a business reply with any message for 24 hours after the customer's last message.
  const windowEndsAt = latestIncomingMessage
    ? new Date(latestIncomingMessage.meta_timestamp || latestIncomingMessage.created_at).getTime() + 24 * 60 * 60 * 1000
    : null;
  const is24HourWindowOpen = windowEndsAt !== null && Date.now() < windowEndsAt;

  const handleDeleteConversation = async (conversationId) => {
    if (!phoneNumberId) {
      throw new Error("Phone number ID is missing.");
    }

    try {
      await conversationService.destroy(phoneNumberId, conversationId);

      setMessages([]);
      setReplyTo(null);

      await onConversationDeleted?.();
    } catch (error) {
      console.error("Unable to delete conversation:", error);
      throw error;
    }
  };

  const handleArchive = async () => {
    if (!phoneNumberId || !activeConversation?.id) return;
    try {
      const isArchived = Boolean(activeConversation.is_archived);
      await (isArchived
        ? conversationService.unarchive(phoneNumberId, activeConversation.id)
        : conversationService.archive(phoneNumberId, activeConversation.id));
      await onConversationDeleted?.(); // refresh + deselect
    } catch (error) {
      console.error("Unable to archive conversation:", error);
    }
  };

  const handleMarkReadClick = () => {
    markedReadRef.current = null;
    markRead();
  };

  if (!activeConversation) {
    return <ChatEmpty />;
  }

  return (
    <main className="wa-chat">
      <ChatHeader
        conversation={activeConversation}
        onProfileClick={onProfileClick}
        onDeleteConversation={handleDeleteConversation}
        onArchive={handleArchive}
        onMarkRead={handleMarkReadClick}
        onBack={onBack}
        realtimeConnected={realtimeConnected}
        windowEndsAt={loading ? undefined : windowEndsAt}
      />

      {/* Messages */}
      {loadError && <div role="alert" className="wa-chat-alert">
        <span>{loadError}</span><button type="button" className="ws-btn ws-btn--plain ws-btn--sm" onClick={loadMessages}>Retry</button>
      </div>}
      <MessageList
        loading={loading}
        messages={messages}
        hasOlder={olderState.hasMore}
        loadingOlder={olderState.loading}
        olderError={olderState.error}
        onLoadOlder={loadOlder}
        onReply={(message) => setReplyTo(message)}
        conversation={activeConversation}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onMessageSent={handleMessageSent}
        onTemplateClick={handleTemplateClick}
        is24HourWindowOpen={is24HourWindowOpen}
        showTemplateSend={showTemplateSend}
        onCloseTemplate={handleCloseTemplate}
        onTemplateSent={handleTemplateSent}
        onSendTemplate={handleSendTemplate}
        contactName={activeConversation?.contact?.name}
      />
    </main>
  );
}

export default ChatWindow;
